<template>
  <v-container fluid>
    <h2 class="page-title mb-1">B2B CLTV</h2>
    <p class="page-caption mb-4">{{ subtitle }}</p>

    <!-- Resumen: una fila de tarjetas por línea de negocio. -->
    <template v-for="line in lines">
      <v-row :key="line.lineaNegocio" dense class="mb-2">
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`CAC · ${line.lineaNegocio}`"
            :firstValue="formatAmount(line.cac)"
            :showCsvButton="false"
            stacked
          >
            <!-- Los dos conteos se abren en un modal con los contratos que
                 los componen: son las cifras que alguien va a querer
                 verificar antes de creerle al CAC. -->
            <template v-slot:second-value>
              <a
                class="drill-link"
                href="#"
                @click.prevent="openContracts(line, 'new')"
              >{{ line.newContracts }} adquisiciones</a>
              · {{ year }}<template v-if="line.renewedContracts">
                ·
                <a
                  class="drill-link"
                  href="#"
                  @click.prevent="openContracts(line, 'renewed')"
                >{{ line.renewedContracts }} renovaciones</a>
                excluidas</template>
            </template>
          </CardResumeCompanyDetails>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`CLTV · ${line.lineaNegocio}`"
            :firstValue="formatAmount(line.cltv)"
            :secondValue="lifetimeLabel(line)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <CardResumeCompanyDetails
            :titulo="`CLTV / CAC · ${line.lineaNegocio}`"
            :firstValue="formatRatio(line.cltvCacRatio)"
            :secondValue="ratioHint(line.cltvCacRatio)"
            :showCsvButton="false"
            stacked
          />
        </v-col>
      </v-row>
    </template>

    <!-- Parámetros cargados a mano: quedan a la vista, no detrás de un modal,
         para que se lea de dónde salen las cifras de arriba. -->
    <v-card outlined class="filters-card mt-6 pa-4">
      <div class="d-flex align-center mb-3">
        <h3 class="section-title">Parámetros</h3>
        <v-spacer></v-spacer>
        <v-select
          v-model="year"
          :items="yearOptions"
          label="Año"
          dense
          hide-details
          outlined
          class="year-select"
          @change="loadData"
        ></v-select>
      </div>

      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.ebCost"
            label="Costo adquisición EB"
            prefix="USD"
            type="number"
            min="0"
            dense
            hide-details
            outlined
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model.number="form.asistenciasCost"
            label="Costo adquisición Asistencias"
            prefix="USD"
            type="number"
            min="0"
            dense
            hide-details
            outlined
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row dense justify="end" class="mt-2">
        <v-col cols="12" md="auto">
          <v-btn
            color="primary"
            block
            :loading="saving"
            :disabled="!isFormValid || loadingData"
            @click="saveParameters"
          >
            Guardar
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-alert
      v-for="warning in warningMessages"
      :key="warning"
      type="warning"
      dense
      text
      class="mt-4 mb-0"
    >
      {{ warning }}
    </v-alert>

    <!-- Detalle: hace auditable cada cifra de las tarjetas. -->
    <v-data-table
      :headers="headers"
      :items="lines"
      :loading="loadingData"
      :items-per-page="-1"
      hide-default-footer
      class="mt-6"
    >
      <template v-slot:item.acquisitionCostUSD="{ item }">
        {{ formatAmount(item.acquisitionCostUSD) }}
      </template>
      <template v-slot:item.cac="{ item }">{{ formatAmount(item.cac) }}</template>
      <template v-slot:item.mrrUSD="{ item }">
        {{ formatAmount(item.mrrUSD) }}
      </template>
      <template v-slot:item.arpuMonthlyUSD="{ item }">
        {{ formatAmount(item.arpuMonthlyUSD) }}
      </template>
      <!-- El churn se abre para ver qué relaciones se contaron como perdidas:
           es la cifra que menos gente va a creer de entrada. -->
      <template v-slot:item.annualChurnRate="{ item }">
        <a
          class="drill-link"
          href="#"
          @click.prevent="openContracts(item, 'churned')"
        >{{ churnLabel(item) }}</a>
      </template>
      <template v-slot:item.exposureYears="{ item }">
        {{ formatExposure(item.exposureYears) }}
      </template>
      <template v-slot:item.expectedLifetimeMonths="{ item }">
        {{ formatMonths(item.expectedLifetimeMonths) }}
      </template>
      <template v-slot:item.cltv="{ item }">
        {{ formatAmount(item.cltv) }}
      </template>
      <template v-slot:item.cltvCacRatio="{ item }">
        {{ formatRatio(item.cltvCacRatio) }}
      </template>
    </v-data-table>

    <p class="footnote mt-4">
      El CLTV se calcula sobre ingresos, sin descontar costos de servicio. Las
      adquisiciones excluyen las renovaciones registradas como tales y los
      contratos internos; una renovación cargada a mano antes de que existiera
      el vínculo, y nunca reclasificada, todavía cuenta como adquisición.
    </p>

    <!-- Detalle de los contratos detrás de un conteo de la tarjeta. -->
    <v-dialog v-model="showContracts" max-width="900">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="dialog-title">{{ contractsTitle }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="showContracts = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <p class="footnote mb-3">
            {{ contractsHint }}
            <template v-if="exposureHint"> {{ exposureHint }}</template>
          </p>

          <v-data-table
            :headers="contractHeaders"
            :items="contracts"
            :loading="loadingContracts"
            :items-per-page="-1"
            hide-default-footer
            dense
            no-data-text="No hay contratos para este conteo."
          >
            <template v-slot:item.status="{ item }">
              <ContractStatusChip :status="item.status" />
            </template>
            <template v-slot:item.endDate="{ item }">
              {{ item.endDate || 'indefinido' }}
            </template>
            <template v-slot:item.previousCodigo="{ item }">
              {{ item.previousCodigo || '—' }}
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { utilsMixin } from '../mixins/utilsMixin';
import CardResumeCompanyDetails from '../components/CardResumeCompanyDetails';
import ContractStatusChip from '../components/ContractStatusChip';
import CacCltvService from '../services/cac-cltv.service';

const LINEA_EB = 'B2B Employee Benefit';
const LINEA_ASISTENCIAS = 'B2B Asistencias';

/** Traducción de los códigos de advertencia que devuelve el backend. */
const WARNING_LABELS = {
  PARAMETERS_NOT_LOADED: 'no tiene parámetros cargados para el año',
  NO_NEW_CONTRACTS: 'no registra contratos nuevos en el año, así que no hay CAC',
  NO_ACTIVE_CONTRACTS: 'no tiene contratos activos, así que no hay ARPU ni CLTV',
  CAC_IS_ZERO: 'tiene el costo de adquisición en 0, así que no hay ratio',
  NO_MRR_SNAPSHOT: 'no tiene snapshot de MRR disponible'
};

/** Número de años hacia atrás que se pueden consultar. */
const YEARS_BACK = 2;

export default {
  name: 'CacCltv',
  mixins: [utilsMixin],
  components: { CardResumeCompanyDetails, ContractStatusChip },
  data() {
    const currentYear = new Date().getFullYear();
    return {
      year: currentYear,
      currentYear,
      lines: [],
      snapshotDate: null,
      loadingData: false,
      saving: false,
      showContracts: false,
      loadingContracts: false,
      contracts: [],
      contractsLine: null,
      contractsKind: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: '',
      form: {
        ebCost: null,
        asistenciasCost: null
      },
      headers: [
        { text: 'Línea de negocio', value: 'lineaNegocio' },
        { text: 'Costo adquisición', value: 'acquisitionCostUSD' },
        { text: 'Adquisiciones', value: 'newContracts' },
        { text: 'Renovaciones', value: 'renewedContracts' },
        { text: 'CAC', value: 'cac' },
        { text: 'MRR actual', value: 'mrrUSD' },
        { text: 'Contratos activos', value: 'activeContracts' },
        { text: 'ARPU mensual', value: 'arpuMonthlyUSD' },
        { text: 'Churn anual', value: 'annualChurnRate' },
        { text: 'Exposición', value: 'exposureYears' },
        { text: 'Vida esperada', value: 'expectedLifetimeMonths' },
        { text: 'CLTV', value: 'cltv' },
        { text: 'CLTV / CAC', value: 'cltvCacRatio' }
      ]
    };
  },
  computed: {
    yearOptions() {
      return Array.from(
        { length: YEARS_BACK + 1 },
        (_, index) => this.currentYear - index
      );
    },
    subtitle() {
      if (!this.snapshotDate) return 'Valores en USD';
      return `MRR al ${this.snapshotDate} · valores en USD`;
    },
    /**
     * La columna del contrato anterior solo lleva dato en las renovaciones;
     * en las adquisiciones sería una columna entera de guiones.
     */
    contractHeaders() {
      const base = [
        { text: 'Código', value: 'codigo' },
        { text: 'Empresa', value: 'empresa' },
        { text: 'País', value: 'pais' },
        { text: 'Inicio', value: 'startDate' },
        { text: 'Término', value: 'endDate' },
        { text: 'Estado', value: 'status' }
      ];
      if (this.contractsKind !== 'renewed') return base;
      return [...base, { text: 'Renueva a', value: 'previousCodigo' }];
    },
    /** Años-relación acumulados, para explicar de dónde sale el churn. */
    exposureHint() {
      const line = this.lines.find(
        (item) => item.lineaNegocio === this.contractsLine
      );
      if (!line || this.contractsKind !== 'churned') return '';
      return `Medido sobre ${this.formatExposure(line.exposureYears)} de exposición, hasta ${line.churnWindowEnd}.`;
    },
    contractsTitle() {
      if (!this.contractsLine) return '';
      const what = {
        renewed: 'Renovaciones',
        churned: 'Bajas',
        new: 'Adquisiciones'
      }[this.contractsKind];
      return `${what} ${this.year} · ${this.contractsLine}`;
    },
    /**
     * Explicita por qué el contrato está en la lista. El modal se abre para
     * responder "¿de dónde sale este número?", y el criterio es la respuesta.
     */
    contractsHint() {
      if (this.contractsKind === 'churned') {
        return 'Relaciones perdidas en el año: contratos cerrados como baja que no tienen sucesor. Los que terminaron según lo pactado no cuentan acá ni en la exposición.';
      }
      if (this.contractsKind === 'renewed') {
        return 'Contratos que suceden a otro contrato. Quedan fuera del CAC porque son el mismo cliente continuando, no una adquisición.';
      }
      return 'Contratos que empezaron en el año y no suceden a ninguno. Son el divisor del CAC. Se excluyen los contratos de tipo demo.';
    },
    /** Una frase por advertencia, con la línea de negocio como sujeto. */
    warningMessages() {
      return this.lines.flatMap((line) =>
        (line.warnings || [])
          .filter((code) => WARNING_LABELS[code])
          .map((code) => `${line.lineaNegocio} ${WARNING_LABELS[code]}.`)
      );
    },
    /** Espejo de la validación del backend, para no depender del 400. */
    isFormValid() {
      const costs = [this.form.ebCost, this.form.asistenciasCost];
      return costs.every((value) => Number.isFinite(value) && value >= 0);
    }
  },
  async created() {
    await this.loadData();
  },
  methods: {
    async loadData() {
      this.loadingData = true;
      const res = await CacCltvService.getMetrics(this.year);
      if (res.status === 'ok') {
        this.lines = res.data.lines;
        this.snapshotDate = res.data.snapshotDate;
        this.syncFormFromLines(res.data.lines);
      } else {
        this.lines = [];
        this.snapshotDate = null;
        this.showErrorSnackbar('Error al cargar los datos de CAC y CLTV');
      }
      this.loadingData = false;
    },

    /** Rellena los inputs con lo que hay guardado para el año seleccionado. */
    syncFormFromLines(lines) {
      const eb = lines.find((line) => line.lineaNegocio === LINEA_EB);
      const asistencias = lines.find(
        (line) => line.lineaNegocio === LINEA_ASISTENCIAS
      );
      this.form = {
        ebCost: eb ? eb.acquisitionCostUSD : null,
        asistenciasCost: asistencias ? asistencias.acquisitionCostUSD : null
      };
    },

    async saveParameters() {
      this.saving = true;
      const res = await CacCltvService.saveParameters(this.year, [
        { lineaNegocio: LINEA_EB, acquisitionCostUSD: this.form.ebCost },
        {
          lineaNegocio: LINEA_ASISTENCIAS,
          acquisitionCostUSD: this.form.asistenciasCost
        }
      ]);
      this.saving = false;

      if (res.status !== 'ok') {
        // No se tocan los inputs: lo que el usuario escribió sigue ahí para
        // que pueda reintentar sin volver a tipear.
        this.showErrorSnackbar('Error al guardar los parámetros');
        return;
      }

      this.showSuccessSnackbar('Parámetros guardados');
      await this.loadData();
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '—';
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: 0
      }).format(Number(value));
    },

    formatRate(rate) {
      if (rate === null || rate === undefined) return '—';
      return `${(Number(rate) * 100).toFixed(2)}%`;
    },

    formatRatio(ratio) {
      if (ratio === null || ratio === undefined) return '—';
      return `${Number(ratio).toFixed(2)}x`;
    },

    /**
     * La vida esperada va en meses para compartir unidad con el MRR y el ARPU.
     * Sin decimales: la precisión que aporta el 0,3 de un mes es falsa cuando
     * el churn del que sale es una estimación cargada a mano.
     */
    formatMonths(months) {
      if (months === null || months === undefined) return '—';
      const rounded = Math.round(Number(months));
      return `${rounded} ${rounded === 1 ? 'mes' : 'meses'}`;
    },

    /**
     * Churn con lo que lo respalda al lado. Un supuesto mostrado igual que una
     * medición se lee como medido, que es justo lo que no puede pasar.
     */
    churnLabel(line) {
      const rate = this.formatRate(line.annualChurnRate);
      if (line.churnIsAssumed) return `${rate} · supuesto`;
      const bajas = line.churnedRelationships;
      return `${rate} · ${bajas} ${bajas === 1 ? 'baja' : 'bajas'}`;
    },

    /** Años-relación observados; es el denominador del churn. */
    formatExposure(years) {
      if (years === null || years === undefined) return '—';
      return `${Number(years).toFixed(1)} años`;
    },

    /**
     * Abre el detalle de uno de los conteos de la línea.
     *
     * Limpia la lista antes de pedir la nueva para que el modal no muestre por
     * un instante los contratos de la línea anterior, que se leerían como los
     * de esta.
     *
     * @param {object} line - Línea de negocio de la tarjeta.
     * @param {'new'|'renewed'} kind - Conteo que se está abriendo.
     */
    async openContracts(line, kind) {
      this.contractsLine = line.lineaNegocio;
      this.contractsKind = kind;
      this.contracts = [];
      this.showContracts = true;
      this.loadingContracts = true;

      const response = await CacCltvService.getContracts(
        this.year,
        line.lineaNegocio,
        kind
      );

      this.loadingContracts = false;
      if (response.status !== 'ok') {
        this.showContracts = false;
        this.showErrorSnackbar('No se pudieron cargar los contratos');
        return;
      }
      this.contracts = response.data.contracts;
    },

    lifetimeLabel(line) {
      if (line.expectedLifetimeMonths === null) {
        return 'vida esperada sin definir';
      }
      const months = this.formatMonths(line.expectedLifetimeMonths);
      if (line.churnIsAssumed) return `vida esperada ${months} · churn supuesto`;
      return `vida esperada ${months}`;
    },

    ratioHint(ratio) {
      if (ratio === null || ratio === undefined) return '';
      return ratio >= 3 ? 'sobre el umbral de 3x' : 'bajo el umbral de 3x';
    }
  }
};
</script>

<style scoped>
/* Colores desde los tokens del sistema (design/README.md). El valor tras la
   coma es el respaldo por si esta rama se mergea antes que la que los declara. */
.page-title {
  color: var(--pw-primary, #104b94);
  font-weight: 700;
}
.page-caption {
  color: var(--pw-text-muted, #8593a3);
  font-size: 0.875rem;
  margin-bottom: 0;
}
.section-title {
  color: var(--pw-text, #1b1d21);
  font-weight: 600;
  font-size: 14px;
}
/* El radio y el fondo de las cards los pone el sistema; fijarlos acá dejaba
   esta tarjeta con las esquinas más cuadradas que el resto de la app. */
.filters-card {
  background-color: var(--pw-surface, #fff);
}
.year-select {
  max-width: 120px;
}
/* Subrayado punteado en vez de color: el texto secundario es gris y teñirlo
   de azul competía visualmente con el monto, que es la cifra principal. */
.drill-link {
  /* Sin esto el enlace se corta entre dos renglones y queda medio subrayado
     arriba y medio abajo, con un área de clic partida en el medio. */
  white-space: nowrap;
  color: inherit;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  cursor: pointer;
}
.drill-link:hover {
  color: var(--pw-primary, #104b94);
  text-decoration-style: solid;
}
.dialog-title {
  color: var(--pw-text, #1b1d21);
  font-weight: 600;
  font-size: 1rem;
}
.footnote {
  color: var(--pw-text-muted, #8593a3);
  font-size: 0.75rem;
  line-height: 1.5;
}
</style>
